import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import VoiceAgent from '@/voice/VoiceAgent'; // şimdilik gizli
import { useVoiceActions } from '@/voice/useVoiceActions';
import ticketsService from '@/services/tickets.service';
import { agentDraftsService } from '@/services/agentDrafts.service';
import agentIcon from '@/assets/agent-icon.png';
import organizationsService from '@/services/organizations.service';
import authService from '@/services/auth.service';
import usersService from '@/services/users.service';
import { transformApiTicketToFrontend } from '@/lib/ticketUtils';
import {
  Phone,
  Video,
  Search,
  User,
  Plus,
  Filter,
  ChevronDown,
  Menu,
  ArrowLeft,
  Home as HomeIcon,
  Settings,
  Edit3,
  GripVertical,
  X,
  Save,
  Moon,
  Sun,
  Send,
  Share2,
  Globe,
  CreditCard,
  Package,
  AlertTriangle,
  CheckCircle,
  Clock,
  LogOut,
  Camera,
  Eye,
  EyeOff,
  Mail,
  FileText,
  Users,
  RefreshCw,
  Archive,
  Bug,
  Megaphone,
  Zap,
  AlertCircle,
  Truck,
  DollarSign,
  RotateCcw,
  UserCheck,
  Building,
  Brain,
  Loader2,
  Shield,
  BarChart3,
  Check,
  Tag,
  CalendarDays,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { isChangelogUnread, CHANGELOG_SEEN_EVENT } from '@/lib/changelog';
import { Switch } from "./ui/switch";
import useAuthStore from "@/stores/authStore";
import useTicketTabsStore from "@/stores/ticketTabsStore";
import TicketTabsBar from "@/components/TicketTabsBar";
import CreateCustomViewDialog from "@/components/CreateCustomViewDialog";
import ShareViewDialog from "@/components/ShareViewDialog";
import { useCustomViews, useDeleteCustomView, useReorderCustomViews, useUpdateCustomView } from "@/hooks/useCustomViews";
import customViewsService from "@/services/customViews.service";
import { AgentBridge } from "@/agent/AgentBridge";
import CloudAgentToggle from "@/components/CloudAgentToggle";
// import PinnedTicketsSection from "@/components/sidebar/PinnedTicketsSection"; // Pins: UI ausgeblendet
import CallStatus from "@/components/voice/CallStatus";
import WhatsAppCard from "@/components/WhatsAppCard";
import MeetingsCard from "@/components/meetings/MeetingsCard";
import { useTranslation } from 'react-i18next';

type ViewType =
  | "tickets"
  | "new"
  | "unassigned"
  | "complaints"
  | "returns"
  | "exchange"
  | "billing"
  | "delivery"
  | "all"
  | "mine"
  | "sla"
  | "spam"
  | "settings";

interface AppShellProps {
  children?: React.ReactNode;
}

interface ViewItem {
  name: string;
  count: number;
  view: ViewType;
  path?: string;
  url?: string; // Custom URL for user-created links
  urgent?: boolean;
  icon?: any;
  iconName?: string; // Icon name for serialization (e.g., "Mail", "Settings")
  isCustom?: boolean; // Flag to identify custom user-created views
  filterConfig?: any; // Filter configuration for ticket count
  viewId?: string; // Custom view ID
  // Sharing fields
  isOwner?: boolean;
  accessType?: 'owned' | 'shared' | 'all';
  ownerFirstName?: string;
  ownerLastName?: string;
}

const AppShell = ({ children }: AppShellProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Fetch custom views
  const { data: customViewsData } = useCustomViews();
  const updateCustomViewMutation = useUpdateCustomView();

  // Search related states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  // Filter für die Ansichten-Liste in der Seitenleiste (rein clientseitig —
  // die Ansichten liegen ohnehin schon vollständig im State).
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isImpersonating, stopImpersonation } = useAuthStore();
  const { openTickets, setLastViewPath } = useTicketTabsStore();
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isTicketListRoute = [
    '/tickets',
    '/tickets/all',
    '/tickets/search',
    '/tickets/new',
    '/tickets/spam',
    '/tickets/unassigned',
    '/tickets/complaints',
    '/tickets/returns',
    '/tickets/exchange',
    '/tickets/billing',
    '/tickets/delivery',
    '/tickets/mine',
    '/tickets/sla',
  ].includes(location.pathname) || location.pathname.startsWith('/tickets/custom/');

  // Die obere Leiste erscheint nur auf wenigen Routen. Der Telefonstatus soll
  // aber überall sichtbar sein — deshalb wird er dort gezeigt, wo die Leiste
  // existiert, und sonst in der Seitenleiste. Nie an beiden Stellen zugleich.
  const showTopHeader =
    !isSettingsRoute &&
    !isTicketListRoute &&
    location.pathname !== '/dashboard' &&
    location.pathname !== '/ki-entwuerfe' &&
    location.pathname !== '/meetings' &&
    location.pathname !== '/anrufe' &&
    location.pathname !== '/kalender' &&
    !(/^\/tickets\/[^/]+$/.test(location.pathname) &&
      !['all', 'spam', 'search', 'custom'].some((route) => location.pathname === `/tickets/${route}`));

  // ANSICHT (liste) rotalarını hatırla — son ticket sekmesi kapatılınca buraya dönülür.
  useEffect(() => {
    const p = location.pathname;
    const isViewRoute =
      p === '/tickets/all' ||
      p === '/tickets/spam' ||
      p === '/tickets/search' ||
      p.startsWith('/tickets/custom/');
    if (isViewRoute) {
      setLastViewPath(p + location.search);
    }
  }, [location.pathname, location.search, setLastViewPath]);
  const [isEditingViews, setIsEditingViews] = useState(false);
  const [originalViewNames, setOriginalViewNames] = useState<Record<string, string>>({});
  const [editingViewData, setEditingViewData] = useState<any>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    email: "",
    customerName: "",
    subject: "",
    content: "",
    priority: "normal" as "low" | "normal" | "high" | "urgent",
    organizationId: "",
  });
  // Default views that are always available
  const getDefaultViews = (): ViewItem[] => [
    { name: t('shell:nav.allTickets', "Alle Tickets"), count: 0, view: "all" as ViewType, path: "/tickets/all", icon: Mail, iconName: "Mail" },
  ];

  // Icon mapping for deserialization
  const iconMap: { [key: string]: any } = {
    Mail,
    AlertTriangle,
    Building,
    UserCheck,
    Clock,
    Brain,
    Zap,
    Settings,
    HomeIcon,
    FileText,
    Users,
    Package,
    Truck,
    DollarSign,
    RefreshCw,
    Archive,
    Shield,
    Send,
    CreditCard,
    CheckCircle,
    AlertCircle,
    RotateCcw,
    Eye,
    EyeOff,
  };

  // Icon options for picker
  const iconOptions = [
    { value: "Mail", label: t('views:f.icoMail', 'Mail'), icon: Mail },
    { value: "AlertTriangle", label: t('views:f.icoWarning', 'Warnung'), icon: AlertTriangle },
    { value: "Building", label: t('views:f.icoBuilding', 'Gebäude'), icon: Building },
    { value: "UserCheck", label: t('views:f.icoUserCheck', 'Benutzer Check'), icon: UserCheck },
    { value: "Clock", label: t('views:f.icoClock', 'Uhr'), icon: Clock },
    { value: "Brain", label: "KI", icon: Brain },
    { value: "Zap", label: t('views:f.icoZap', 'Blitz'), icon: Zap },
    { value: "Settings", label: t('views:u.uSettings', 'Einstellungen'), icon: Settings },
    { value: "HomeIcon", label: t('views:f.icoHome', 'Home'), icon: HomeIcon },
    { value: "FileText", label: t('views:f.icoDoc', 'Dokument'), icon: FileText },
    { value: "Users", label: t('common:field.users', 'Benutzer'), icon: Users },
    { value: "Package", label: t('views:f.icoPackage', 'Paket'), icon: Package },
    { value: "Truck", label: t('views:f.icoTruck', 'Lieferung'), icon: Truck },
    { value: "DollarSign", label: t('views:f.icoMoney', 'Geld'), icon: DollarSign },
    { value: "RefreshCw", label: t('common:action.refresh', "Aktualisieren"), icon: RefreshCw },
    { value: "Archive", label: t('views:f.icoArchive', 'Archiv'), icon: Archive },
    { value: "Shield", label: t('views:f.icoShield', 'Schild'), icon: Shield },
    { value: "Send", label: t('common:action.send', "Senden"), icon: Send },
    { value: "CreditCard", label: t('views:f.icoCard', 'Kreditkarte'), icon: CreditCard },
    { value: "CheckCircle", label: t('views:f.icoCheck', 'Check'), icon: CheckCircle },
    { value: "AlertCircle", label: t('views:f.icoAlert', 'Alert'), icon: AlertCircle },
    { value: "RotateCcw", label: "Zurücksetzen", icon: RotateCcw },
  ];

  // Load views from localStorage or use defaults
  const [views, setViews] = useState<ViewItem[]>(() => {
    try {
      const saved = localStorage.getItem('sidebarViews');
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaults = getDefaultViews();
        // Deserialize icons
        return parsed.map((view: ViewItem) => {
          // System-Ansichten (ohne viewId, z. B. "Alle Tickets"): Name IMMER aus dem Code nehmen,
          // damit gespeicherte alte Beschriftungen die übersetzten Namen nicht überschreiben.
          // Vom Benutzer erstellte/umbenannte Ansichten (mit viewId) behalten ihren Namen.
          const def = !view.viewId ? defaults.find(d => d.view === view.view) : undefined;
          return {
            ...view,
            ...(def ? { name: def.name } : {}),
            icon: view.iconName ? iconMap[view.iconName] : Mail,
          };
        });
      }
    } catch (error) {
      console.error('Failed to load saved views:', error);
    }
    return getDefaultViews();
  });
  const [editingViews, setEditingViews] = useState<ViewItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);

  // --- Sesli agent: uygulama geneli navigasyon action'ları ---
  useVoiceActions([
    {
      id: 'open_view',
      description:
        'Öffnet eine Ticket-Ansicht/Liste anhand ihres Namens (z.B. "Alle Tickets", "Spam" oder eine eigene Ansicht).',
      params: [{ name: 'name', type: 'string', description: 'Name der Ansicht', required: true }],
      run: ({ name }) => {
        const q = String(name || '').toLowerCase().trim();
        if (q.includes('spam')) {
          navigate('/tickets/spam');
          return 'Spam-Ansicht geöffnet.';
        }
        const match = views.find((v) => v.name.toLowerCase().includes(q));
        if (match) {
          navigate(match.path || `/tickets/${match.view}`);
          return `Ansicht "${match.name}" geöffnet.`;
        }
        navigate('/tickets/all');
        return 'Ansicht nicht gefunden, alle Tickets geöffnet.';
      },
    },
    {
      id: 'search_tickets',
      description: 'Durchsucht Tickets nach einem Suchbegriff (Kunde, Bestellnummer, Betreff …).',
      params: [{ name: 'query', type: 'string', description: 'Suchbegriff', required: true }],
      getTarget: () => document.querySelector('[data-voice="search"]') as HTMLElement | null,
      run: ({ query }) => {
        const term = String(query || '').trim();
        if (!term) return 'Kein Suchbegriff angegeben.';
        navigate(`/tickets/search?q=${encodeURIComponent(term)}`);
        return `Suche nach "${term}" gestartet.`;
      },
    },
    {
      id: 'open_dashboard',
      description: 'Öffnet das Dashboard mit Statistiken und Diagrammen.',
      run: () => {
        navigate('/dashboard');
        return 'Dashboard geöffnet.';
      },
    },
    {
      id: 'open_settings',
      description: 'Öffnet die Einstellungen.',
      run: () => {
        navigate('/settings');
        return 'Einstellungen geöffnet.';
      },
    },
    {
      id: 'open_ticket_by_number',
      description: 'Öffnet ein Ticket anhand seiner Ticketnummer direkt in der Detailansicht.',
      params: [{ name: 'number', type: 'string', description: 'Ticketnummer', required: true }],
      run: async ({ number }) => {
        const n = String(number || '').trim();
        try {
          const res = await ticketsService.searchTickets({ q: n, limit: 5, mode: 'autocomplete' } as any);
          const first = res?.data?.tickets?.[0];
          if (first?.id) {
            navigate(`/tickets/${first.id}`);
            return `Ticket ${first.ticket_number || n} geöffnet.`;
          }
          navigate(`/tickets/search?q=${encodeURIComponent(n)}`);
          return `Kein direktes Ticket gefunden — Suche nach ${n} geöffnet.`;
        } catch {
          navigate(`/tickets/search?q=${encodeURIComponent(n)}`);
          return `Suche nach Ticket ${n} geöffnet.`;
        }
      },
    },
  ]);
  const [deleteConfirmView, setDeleteConfirmView] = useState<{ index: number; view: ViewItem } | null>(null);
  const [shareViewDialog, setShareViewDialog] = useState<{ viewId: string; viewName: string } | null>(null);

  // Separate state for view counts to prevent race conditions
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [countsLoaded, setCountsLoaded] = useState(false); // Show skeleton until first load complete
  const viewCountsFetchingRef = useRef(false); // Prevent concurrent fetches

  // Custom view mutations
  const deleteViewMutation = useDeleteCustomView();
  const reorderViewsMutation = useReorderCustomViews();
  const [newViewForm, setNewViewForm] = useState({
    name: "",
    iconName: "Mail",
    statuses: [] as string[],
    assigneeType: "all" as "all" | "unassigned" | "me" | "specific",
    assigneeId: "",
    organizationIds: [] as string[],
    priority: "all" as string,
    sortBy: "created_at" as string,
    sortOrder: "desc" as string,
  });
  const [selectedFilterField, setSelectedFilterField] = useState<string>("");
  const [activeFilterConfig, setActiveFilterConfig] = useState<{
    type?: string;
    operator: string;
    value:
      | string
      | string[]
      | boolean
      | number
      | { from?: string; to?: string };
  } | null>(null);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    isLoading: false,
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Dark mode effect
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldUseDark = savedTheme ? savedTheme === "dark" : true;
    if (shouldUseDark) {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
      if (!savedTheme) localStorage.setItem("theme", "dark");
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Load custom views from API and merge with default views
  useEffect(() => {
    if (customViewsData?.data?.views) {
      const customViews: ViewItem[] = customViewsData.data.views.map((view: any) => ({
        name: view.name,
        count: 0,
        view: `custom-${view.id}` as ViewType,
        path: `/tickets/custom/${view.id}`,
        icon: iconMap[view.icon_name] || Mail,
        iconName: view.icon_name,
        isCustom: true,
        filterConfig: view.filter_config,
        viewId: view.id,
        // Sharing fields
        isOwner: view.is_owner ?? true,
        accessType: view.access_type ?? 'owned',
        ownerFirstName: view.owner_first_name,
        ownerLastName: view.owner_last_name,
      }));

      // Get default views
      const defaultViews = getDefaultViews();

      // Check localStorage for view order/visibility preferences (but NOT counts)
      // IMPORTANT: filterConfig must come from API, not localStorage (it may change)
      let initialViews: ViewItem[] = [...defaultViews, ...customViews];
      try {
        const saved = localStorage.getItem('sidebarViews');
        if (saved) {
          // Create a map of custom views from API for quick lookup
          const customViewsMap = new Map(customViews.map(cv => [cv.view, cv]));

          const savedViews = JSON.parse(saved).map((view: ViewItem) => {
            // For custom views, merge with fresh data from API (especially filterConfig)
            if (view.isCustom && customViewsMap.has(view.view)) {
              const apiView = customViewsMap.get(view.view)!;
              return {
                ...view,
                icon: view.iconName ? iconMap[view.iconName] : Mail,
                count: 0,
                // CRITICAL: Always use filterConfig from API, not localStorage
                filterConfig: apiView.filterConfig,
                viewId: apiView.viewId,
              };
            }
            // System-Ansicht (nicht custom): Name IMMER aus dem Code (übersetzbar),
            // niemals aus dem localStorage-Snapshot.
            const def = !view.isCustom ? defaultViews.find(d => d.view === view.view) : undefined;
            return {
              ...view,
              ...(def ? { name: def.name } : {}),
              icon: view.iconName ? iconMap[view.iconName] : Mail,
              count: 0,
            };
          });

          // Only add custom views that aren't already in saved views
          const existingCustomIds = new Set(savedViews.filter((v: ViewItem) => v.isCustom).map((v: ViewItem) => v.view));
          const newCustomViews = customViews.filter(cv => !existingCustomIds.has(cv.view));

          // Filter out saved custom views that no longer exist in API
          const validSavedViews = savedViews.filter((v: ViewItem) =>
            !v.isCustom || customViewsMap.has(v.view)
          );

          initialViews = [...validSavedViews, ...newCustomViews];
        }
      } catch (error) {
        console.error('Failed to load saved views:', error);
      }

      setViews(initialViews);
    }
  }, [customViewsData]);

  // Function to fetch view counts using batch API - single request for all views
  const fetchViewCounts = React.useCallback(async (viewsToFetch: ViewItem[]) => {
    // Prevent concurrent fetches
    if (viewCountsFetchingRef.current || viewsToFetch.length === 0) return;
    viewCountsFetchingRef.current = true;

    try {
      // Get custom views with their filter configs
      const customViews = viewsToFetch.filter(v => v.isCustom && v.filterConfig && v.viewId);

      // If no custom views with filterConfig, just mark as loaded and return
      if (customViews.length === 0) {
        setCountsLoaded(true);
        viewCountsFetchingRef.current = false;
        return;
      }

      // Prepare batch request - single API call for all views
      const batchViews = customViews.map(v => ({
        viewId: v.viewId!,
        filterConfig: v.filterConfig
      }));

      // Fetch all counts in a single batch request
      const response = await ticketsService.batchCount(batchViews);

      if (response.success && response.data?.counts) {
        const newCounts: Record<string, number> = {};

        // Map viewId back to view key (custom-{viewId})
        customViews.forEach(v => {
          const count = response.data.counts[v.viewId!];
          if (count !== undefined) {
            newCounts[v.view] = count;
          }
        });

        // Add "all" count from batch response
        if (response.data.counts['all'] !== undefined) {
          newCounts['all'] = response.data.counts['all'];
        }

        // Update counts state - merge with existing to preserve any counts we didn't fetch
        setViewCounts(prev => ({ ...prev, ...newCounts }));
        setCountsLoaded(true); // Mark counts as loaded after first successful fetch
      }
    } catch (error) {
      console.error('Failed to fetch view counts:', error);
      // On error, don't update counts - keep existing values
      // But still mark as loaded to show 0 instead of skeleton forever
      setCountsLoaded(true);
    } finally {
      viewCountsFetchingRef.current = false;
    }
  }, []);

  // Fetch counts on initial load and when views change
  useEffect(() => {
    if (views.length > 0) {
      fetchViewCounts(views);
    }
  }, [views, fetchViewCounts]);

  // Listen for ticket changes and refetch counts
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // When tickets or tickets-stats queries are invalidated or updated, refetch view counts
      if (event.type === 'updated' && event.query.queryKey[0] === 'tickets-stats') {
        fetchViewCounts(views);
      }
    });

    return () => unsubscribe();
  }, [queryClient, fetchViewCounts, views]);

  // Auto-refresh view counts every 60 seconds
  useEffect(() => {
    if (views.length === 0) return;

    const intervalId = setInterval(() => {
      fetchViewCounts(views);
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [views, fetchViewCounts]);

  // Debounced search effect (autocomplete dropdown)
  useEffect(() => {
    const trimmed = searchQuery.trim();

    // Min 2 karakter: daha kısa sorgular gereksiz yük oluşturuyor
    if (trimmed.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      setShowSearchResults(false);
      return;
    }

    // Yarış iptali: yeni harf yazıldığında önceki isteği iptal et
    const controller = new AbortController();
    setIsSearching(true);

    const timer = setTimeout(async () => {
      try {
        const response = await ticketsService.searchTickets({
          q: trimmed,
          // İçerik araması backend'de FTS ile her zaman açık — ayrı flag göndermiyoruz
          limit: 10,
          mode: 'autocomplete',
          signal: controller.signal
        });
        const transformedResults = response.data.tickets.map(transformApiTicketToFrontend);
        setSearchResults(transformedResults);
        setShowSearchResults(true);
      } catch (error: any) {
        // Abort edilen isteklerde sessiz kal
        const isCanceled =
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED';
        if (!isCanceled) {
          console.error('Search error:', error);
          setSearchResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  // Update dropdown position when it opens or on window resize
  useEffect(() => {
    if (showSearchResults && searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [showSearchResults]);

  // Update dropdown position on window resize
  useEffect(() => {
    if (!showSearchResults) return;

    const handleResize = () => {
      if (searchInputRef.current) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [showSearchResults]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // Toggle sidebar collapse state
  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);

    if (newDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const handleEditViews = () => {
    setEditingViews([...views]);
    // Store original names for custom views to track changes
    const originalNames: Record<string, string> = {};
    views.forEach(view => {
      if (view.viewId) {
        originalNames[view.viewId] = view.name;
      }
    });
    setOriginalViewNames(originalNames);
    setIsEditingViews(true);
  };

  const handleSaveViews = async () => {
    setViews(editingViews);
    setIsEditingViews(false);

    // Persist custom views order to database
    const customViewsToReorder = editingViews
      .map((view, index) => {
        if (view.isCustom && view.view.startsWith('custom-')) {
          const viewId = view.view.replace('custom-', '');
          return { id: viewId, position: index };
        }
        return null;
      })
      .filter(Boolean) as { id: string; position: number }[];

    if (customViewsToReorder.length > 0) {
      try {
        await reorderViewsMutation.mutateAsync(customViewsToReorder);
      } catch (error) {
        console.error('Failed to save custom view order to database:', error);
      }
    }

    // Persist to localStorage
    try {
      const serialized = editingViews.map(view => ({
        ...view,
        icon: undefined, // Remove icon component (not serializable)
        // iconName is already there for deserialization
      }));
      localStorage.setItem('sidebarViews', JSON.stringify(serialized));
    } catch (error) {
      console.error('Failed to save views to localStorage:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingViews([]);
    setIsEditingViews(false);
  };

  const handleResetToDefault = () => {
    if (confirm('Möchten Sie wirklich alle Ansichten auf die Standardeinstellungen zurücksetzen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      const defaultViews = getDefaultViews();
      setEditingViews(defaultViews);
      setViews(defaultViews);
      localStorage.removeItem('sidebarViews');
      setIsEditingViews(false);
    }
  };

  const handleViewNameChange = (index: number, newName: string) => {
    const updated = [...editingViews];
    updated[index].name = newName;
    setEditingViews(updated);
  };

  const handleUpdateViewName = async (viewId: string, newName: string) => {
    if (!viewId) return;

    try {
      await updateCustomViewMutation.mutateAsync({
        id: viewId,
        data: { name: newName }
      });
      // Update original name after successful save
      setOriginalViewNames(prev => ({ ...prev, [viewId]: newName }));
    } catch (error) {
      console.error('Failed to update custom view name:', error);
    }
  };

  const handleEditViewConfig = (viewItem: ViewItem) => {
    if (!viewItem.viewId) return;

    setEditingViewData({
      id: viewItem.viewId,
      name: viewItem.name,
      iconName: viewItem.iconName || 'Mail',
      filterConfig: viewItem.filterConfig || { filters: [] },
      sortConfig: { sortBy: 'created_at', sortOrder: 'desc' }
    });
    setIsCreateViewOpen(true);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const updated = [...editingViews];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);
    setEditingViews(updated);
    setDraggedIndex(null);
  };

  const handleRemoveView = (index: number) => {
    const viewToRemove = editingViews[index];
    if (viewToRemove.isCustom) {
      // Show confirmation dialog for custom views
      setDeleteConfirmView({ index, view: viewToRemove });
    } else {
      // Default views can be removed directly from list
      const updated = editingViews.filter((_, i) => i !== index);
      setEditingViews(updated);
    }
  };

  const handleConfirmDeleteView = async () => {
    if (!deleteConfirmView) return;

    const { index, view } = deleteConfirmView;

    // If it's a custom view (from database), delete it from API
    if (view.isCustom && view.view.startsWith('custom-')) {
      const viewId = view.view.replace('custom-', '');
      try {
        await deleteViewMutation.mutateAsync(viewId);
      } catch (error) {
        console.error('Failed to delete custom view:', error);
        setDeleteConfirmView(null);
        return;
      }
    }

    // Remove from editing views
    const updated = editingViews.filter((_, i) => i !== index);
    setEditingViews(updated);
    setDeleteConfirmView(null);
  };

  const handleCreateView = () => {
    setIsCreateViewOpen(true);
  };

  const handleShareView = (viewItem: ViewItem) => {
    if (!viewItem.viewId) return;
    setShareViewDialog({
      viewId: viewItem.viewId,
      viewName: viewItem.name,
    });
  };

  const handleNewViewFormChange = (field: string, value: any): void => {
    setNewViewForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleStatus = (status: string): void => {
    setNewViewForm((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status]
    }));
  };

  const toggleOrganization = (orgId: string): void => {
    setNewViewForm((prev) => ({
      ...prev,
      organizationIds: prev.organizationIds.includes(orgId)
        ? prev.organizationIds.filter(id => id !== orgId)
        : [...prev.organizationIds, orgId]
    }));
  };

  const getOperatorOptions = (
    fieldType?: string,
  ): Array<{ value: string; label: string }> => {
    const baseOptions = [
      { value: "contains", label: t('views:op.contains', "enthält") },
      { value: "not_contains", label: t('views:op.notContains', "enthält nicht") },
      { value: "equals", label: t('views:op.equals', "ist gleich") },
      { value: "not_equals", label: "ist nicht gleich" },
    ];

    if (fieldType === "number" || fieldType === "daterange") {
      return [
        ...baseOptions,
        { value: "greater_than", label: t('views:op.gt', "größer als") },
        { value: "less_than", label: t('views:op.lt', "kleiner als") },
        { value: "between", label: "zwischen" },
      ];
    }

    if (fieldType === "boolean") {
      return [
        { value: "equals", label: "ist" },
        { value: "not_equals", label: t('views:op.notEquals', "ist nicht") },
      ];
    }

    if (fieldType === "multiselect") {
      return [
        { value: "in", label: t('views:op.isOneOf', "ist einer von") },
        { value: "not_in", label: t('views:op.isNoneOf', "ist keiner von") },
      ];
    }

    return baseOptions;
  };

  const renderValueInput = (): JSX.Element => {
    const fieldType = filterFieldOptions.find(
      (f) => f.value === selectedFilterField,
    )?.type;
    const operator = activeFilterConfig?.operator;

    if (fieldType === "multiselect") {
      const options = getFieldOptions(selectedFilterField);
      return (
        <Select
          value={
            typeof activeFilterConfig?.value === "string"
              ? activeFilterConfig.value
              : ""
          }
          onValueChange={(value) => {
            setActiveFilterConfig((prev) => (prev ? { ...prev, value } : null));
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('shell:ph.valueSelect', 'Wert auswählen')} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (fieldType === "boolean") {
      return (
        <Select
          value={
            typeof activeFilterConfig?.value === "boolean"
              ? activeFilterConfig.value.toString()
              : ""
          }
          onValueChange={(value) => {
            setActiveFilterConfig((prev) =>
              prev
                ? {
                    ...prev,
                    value: value === "true",
                  }
                : null,
            );
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('shell:ph.valueSelect', 'Wert auswählen')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Ja</SelectItem>
            <SelectItem value="false">{t('shell:action.no', 'Nein')}</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (fieldType === "daterange" && operator === "between") {
      return (
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            value={
              typeof activeFilterConfig?.value === "object" &&
              activeFilterConfig.value !== null &&
              "from" in activeFilterConfig.value &&
              typeof activeFilterConfig.value === "object"
                ? (activeFilterConfig.value as { from?: string }).from || ""
                : ""
            }
            onChange={(e) => {
              setActiveFilterConfig((prev) =>
                prev
                  ? {
                      ...prev,
                      value: {
                        ...(typeof prev.value === "object" &&
                        prev.value !== null
                          ? (prev.value as { from?: string; to?: string })
                          : {}),
                        from: e.target.value,
                      },
                    }
                  : null,
              );
            }}
          />
          <Input
            type="date"
            value={
              typeof activeFilterConfig?.value === "object" &&
              activeFilterConfig.value !== null &&
              "to" in activeFilterConfig.value &&
              typeof activeFilterConfig.value === "object"
                ? (activeFilterConfig.value as { to?: string }).to || ""
                : ""
            }
            onChange={(e) => {
              setActiveFilterConfig((prev) =>
                prev
                  ? {
                      ...prev,
                      value: {
                        ...(typeof prev.value === "object" &&
                        prev.value !== null
                          ? (prev.value as { from?: string; to?: string })
                          : {}),
                        to: e.target.value,
                      },
                    }
                  : null,
              );
            }}
          />
        </div>
      );
    }

    if (fieldType === "daterange") {
      return (
        <Input
          type="date"
          value={
            typeof activeFilterConfig?.value === "string"
              ? activeFilterConfig.value
              : ""
          }
          onChange={(e) => {
            setActiveFilterConfig((prev) =>
              prev
                ? {
                    ...prev,
                    value: e.target.value,
                  }
                : null,
            );
          }}
        />
      );
    }

    if (fieldType === "number") {
      return (
        <Input
          type="number"
          placeholder={t('shell:ph.number', 'Zahl eingeben')}
          value={
            typeof activeFilterConfig?.value === "string"
              ? activeFilterConfig.value
              : ""
          }
          onChange={(e) => {
            setActiveFilterConfig((prev) =>
              prev
                ? {
                    ...prev,
                    value: e.target.value,
                  }
                : null,
            );
          }}
        />
      );
    }

    return (
      <Input
        placeholder={t('shell:ph.value', 'Wert eingeben')}
        value={
          typeof activeFilterConfig?.value === "string"
            ? activeFilterConfig.value
            : ""
        }
        onChange={(e) => {
          setActiveFilterConfig((prev) =>
            prev ? { ...prev, value: e.target.value } : null,
          );
        }}
      />
    );
  };

  const handleAddFilter = (): void => {
    if (
      selectedFilterField &&
      activeFilterConfig?.operator &&
      activeFilterConfig?.value
    ) {
      const fieldLabel =
        filterFieldOptions.find((f) => f.value === selectedFilterField)
          ?.label || selectedFilterField;
      const operatorLabel =
        getOperatorOptions().find(
          (o) => o.value === activeFilterConfig.operator,
        )?.label || activeFilterConfig.operator;
      const valueLabel = Array.isArray(activeFilterConfig.value)
        ? activeFilterConfig.value.join(", ")
        : typeof activeFilterConfig.value === "object" &&
            activeFilterConfig.value !== null
          ? JSON.stringify(activeFilterConfig.value)
          : String(activeFilterConfig.value);

      const newFilter = {
        id: Date.now().toString(),
        field: selectedFilterField,
        operator: activeFilterConfig.operator,
        value: activeFilterConfig.value,
        label: `${fieldLabel} ${operatorLabel} ${valueLabel}`,
      };

      setNewViewForm((prev) => ({
        ...prev,
        filters: [...prev.filters, newFilter],
      }));

      // Reset form
      setSelectedFilterField("");
      setActiveFilterConfig(null);
    }
  };

  const addFilter = (
    field: string,
    operator: string,
    value:
      | string
      | string[]
      | boolean
      | number
      | { from?: string; to?: string },
    label: string,
  ): void => {
    const newFilter = {
      id: Date.now().toString(),
      field,
      operator,
      value,
      label,
    };
    setNewViewForm((prev) => ({
      ...prev,
      filters: [...prev.filters, newFilter],
    }));
    setSelectedFilterField("");
    setActiveFilterConfig(null);
  };

  const removeFilter = (filterId: string): void => {
    setNewViewForm((prev) => ({
      ...prev,
      filters: prev.filters.filter((f) => f.id !== filterId),
    }));
  };

  const filterFieldOptions = [
    { value: "status", label: t('common:field.status', 'Status'), type: "multiselect" },
    { value: "priority", label: t('ticketList:column.priority', "Priorität"), type: "multiselect" },
    { value: "assignee", label: t('views:f.fAssignee', 'Zugewiesen an'), type: "multiselect" },
    { value: "shop", label: t('views:f.icoShop', 'Shop'), type: "multiselect" },
    { value: "category", label: t('common:field.category', 'Kategorie'), type: "multiselect" },
    { value: "source", label: t('views:f.fSource', 'Ticket-Quelle'), type: "multiselect" },
    { value: "email_subject", label: t('views:f.fEmailSubject', 'E-Mail Betreff'), type: "text" },
    { value: "email_from", label: t('views:f.fEmailFrom', 'E-Mail Absender'), type: "text" },
    { value: "customer_name", label: t('ticket:section.customerName', "Kundenname"), type: "text" },
    { value: "ticket_id", label: t('views:f.fTicketId', 'Ticket-ID'), type: "text" },
    { value: "order_number", label: t('views:f.fOrderNumber', 'Bestellnummer'), type: "text" },
    { value: "customer_email", label: t('views:f.fCustomerEmail', 'Kunden-E-Mail'), type: "text" },
    { value: "created_date", label: t('views:f.fCreatedDate', 'Erstellungsdatum'), type: "daterange" },
    { value: "updated_date", label: t('views:f.fUpdatedDate', 'Aktualisierungsdatum'), type: "daterange" },
    { value: "due_date", label: t('views:f.fDueDate', 'Fälligkeitsdatum'), type: "daterange" },
    { value: "message_count", label: t('views:f.fMessageCount', 'Anzahl Nachrichten'), type: "number" },
    { value: "has_attachments", label: t('views:f.fHasAttachments', 'Hat Anhänge'), type: "boolean" },
    { value: "is_unread", label: t('views:f.fUnread', 'Ungelesen'), type: "boolean" },
    { value: "sla_alert", label: t('views:f.fSlaAlert', 'SLA-Warnung'), type: "boolean" },
  ];

  const getFieldOptions = (
    fieldType: string,
  ): Array<{ value: string; label: string }> => {
    switch (fieldType) {
      case "status":
        return [
          { value: "new", label: t('domain:status.new', "Neu") },
          { value: "open", label: t('domain:status.open', "Offen") },
          { value: "pending", label: t('domain:status.pending', "Wartend") },
          { value: "in_progress", label: t('domain:status.in_progress', "In Bearbeitung") },
          { value: "closed", label: t('views:f.sCompleted', 'Abgeschlossen') },
        ];
      case "priority":
        return [
          { value: "low", label: t('domain:priority.low', "Niedrig") },
          { value: "normal", label: t('domain:priority.normal', "Normal") },
          { value: "high", label: t('domain:priority.high', "Hoch") },
          { value: "urgent", label: t('domain:priority.urgent', "Dringend") },
        ];
      case "assignee":
        return [
          { value: "sarah", label: "Sarah Weber" },
          { value: "michael", label: "Michael Krause" },
          { value: "anna", label: "Anna Fischer" },
          { value: "david", label: "David Klein" },
          { value: "unassigned", label: t('ticket:ph.unassigned', "Nicht zugewiesen") },
        ];
      case "shop":
        return [
          { value: "shop1", label: "shop1.de" },
          { value: "shop2", label: "shop2.de" },
          { value: "shop3", label: "shop3.de" },
        ];
      case "category":
        return [
          { value: "general", label: t('common:field.general', "Allgemein") },
        ];
      case "source":
        return [
          { value: "email", label: t('views:f.icoEmail', 'E-Mail') },
          { value: "web", label: "Web-Formular" },
          { value: "phone", label: t('views:f.icoPhone', 'Telefon') },
          { value: "chat", label: t('views:f.icoChat', 'Chat') },
          { value: "social", label: t('views:f.icoSocial', 'Social Media') },
        ];
      default:
        return [];
    }
  };

  const handleSaveNewView = (): void => {
    if (!newViewForm.name.trim()) return;

    // Build URL with query parameters
    const params = new URLSearchParams();

    if (newViewForm.statuses.length > 0) {
      params.set('status', newViewForm.statuses.join(','));
    }

    if (newViewForm.assigneeType === 'unassigned') {
      params.set('assigneeId', 'unassigned');
    } else if (newViewForm.assigneeType === 'me' && user?.id) {
      params.set('assigneeId', user.id);
    } else if (newViewForm.assigneeType === 'specific' && newViewForm.assigneeId) {
      params.set('assigneeId', newViewForm.assigneeId);
    }

    if (newViewForm.organizationIds.length > 0) {
      params.set('organizationId', newViewForm.organizationIds.join(','));
    }

    if (newViewForm.priority && newViewForm.priority !== 'all') {
      params.set('priority', newViewForm.priority);
    }

    if (newViewForm.sortBy && newViewForm.sortBy !== 'created_at') {
      params.set('sortBy', newViewForm.sortBy);
    }

    if (newViewForm.sortOrder && newViewForm.sortOrder !== 'desc') {
      params.set('sortOrder', newViewForm.sortOrder);
    }

    const queryString = params.toString();
    const url = queryString ? `/tickets/all?${queryString}` : '/tickets/all';

    const newView: ViewItem = {
      name: newViewForm.name,
      count: 0,
      view: "custom" as ViewType,
      path: url,
      iconName: newViewForm.iconName,
      icon: iconMap[newViewForm.iconName] || Mail,
      isCustom: true,
    };

    setEditingViews([...editingViews, newView]);
    setNewViewForm({
      name: "",
      iconName: "Mail",
      statuses: [],
      assigneeType: "all",
      assigneeId: "",
      organizationIds: [],
      priority: "all",
      sortBy: "created_at",
      sortOrder: "desc",
    });
    setIsCreateViewOpen(false);
  };

  const handleCancelNewView = (): void => {
    setNewViewForm({
      name: "",
      iconName: "Mail",
      statuses: [],
      assigneeType: "all",
      assigneeId: "",
      organizationIds: [],
      priority: "all",
      sortBy: "created_at",
      sortOrder: "desc",
    });
    setIsCreateViewOpen(false);
  };

  // Fetch organizations for ticket creation
  const { data: orgsResponse } = useQuery({
    queryKey: ['organizations-list'],
    queryFn: () => organizationsService.getOrganizations(),
    staleTime: 10 * 60 * 1000,
  });

  // KI-Entwürfe bekleyen sayısı (nav rozeti)
  const { data: kiDraftCount = 0 } = useQuery({
    queryKey: ['agent-drafts-count'],
    queryFn: () => agentDraftsService.count(),
    refetchInterval: 30000,
  });

  // Fetch agents for view creation
  const { data: agentsListResponse } = useQuery({
    queryKey: ['agents-for-views'],
    queryFn: () => usersService.getAgents({ includeStats: false, status: 'active' }),
    staleTime: 10 * 60 * 1000,
  });

  const handleTicketFormChange = (field: string, value: string): void => {
    setTicketForm((prev) => ({ ...prev, [field]: value }));
  };

  // Mutation for creating ticket
  const createTicketMutation = useMutation({
    mutationFn: (data: typeof ticketForm) => ticketsService.createTicket({
      customerEmail: data.email,
      customerName: data.customerName,
      subject: data.subject,
      content: data.content,
      priority: data.priority,
      organizationId: data.organizationId,
      sendEmail: true
    }),
    onSuccess: (response) => {
      // console.log('✅ Ticket created successfully:', response.data.ticket.ticket_number);
      // Invalidate tickets list
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      // Reset form and close
      setTicketForm({
        email: "",
        customerName: "",
        subject: "",
        content: "",
        priority: "normal",
        organizationId: "",
      });
      setIsCreateTicketOpen(false);
      // Navigate to the new ticket
      navigate(`/tickets/${response.data.ticket.id}`);
    },
    onError: (error: any) => {
      console.error('❌ Failed to create ticket:', error);
      const errorMessage = error?.response?.data?.message || 'Ticket konnte nicht erstellt werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  const handleSendTicket = (): void => {
    if (!ticketForm.email || !ticketForm.subject || !ticketForm.content || !ticketForm.organizationId) {
      alert(t('views:u.mFillRequired', 'Bitte füllen Sie alle erforderlichen Felder aus'));
      return;
    }
    createTicketMutation.mutate(ticketForm);
  };

  // Fehler melden: erstellt direkt ein Ticket an bug@vyndesk.com (uns) und öffnet es.
  // Der Nutzer beschreibt den Fehler dann als Antwort im Ticket-Detail – die Antwort
  // geht per E-Mail an bug@vyndesk.com.
  const BUG_REPORT_EMAIL = 'bug@vyndesk.com';
  const [isReportingBug, setIsReportingBug] = useState(false);

  // Changelog okunmadı-rozeti: en yeni entry id localStorage'takinden
  // farklıysa Megafon ikonu yanıp söner; sayfa açılınca event ile söner.
  const [changelogUnread, setChangelogUnread] = useState<boolean>(() => isChangelogUnread());
  useEffect(() => {
    const refresh = () => setChangelogUnread(isChangelogUnread());
    window.addEventListener(CHANGELOG_SEEN_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(CHANGELOG_SEEN_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const handleReportBug = async (): Promise<void> => {
    if (isReportingBug) return;
    // ÖNEMLİ: /organizations TÜM org'ları döner (en yenisi önce) — [0] üyesi
    // olmadığımız bir org olabilir; ticket orada oluşursa tenant-isolation
    // yüzünden "Ticket bulunamadı" olur. Üyesi olduğumuz ilk org'u seç.
    const orgs = orgsResponse?.data ?? [];
    const ownOrg = orgs.find((org: any) => org.is_member) ?? orgs[0];
    const orgId = ownOrg?.id;
    if (!orgId) {
      alert(t('views:u.mNoOrgTicket', 'Keine Organisation gefunden, Fehler-Ticket kann nicht erstellt werden.'));
      return;
    }
    setIsReportingBug(true);
    try {
      const deskName = ownOrg?.name || 'Vyndesk';
      const response = await ticketsService.createTicket({
        customerEmail: BUG_REPORT_EMAIL,
        customerName: 'Vyndesk Support',
        subject: `Fehler melden – ${deskName}`,
        content: 'Bitte beschreiben Sie den Fehler in Ihrer Antwort an uns.',
        priority: 'high',
        organizationId: orgId,
        sendEmail: false,
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/tickets/${response.data.ticket.id}`);
    } catch (error: any) {
      console.error('❌ Failed to create bug ticket:', error);
      alert(error?.response?.data?.message || 'Fehler-Ticket konnte nicht erstellt werden');
    } finally {
      setIsReportingBug(false);
    }
  };

  const handleCancelTicket = (): void => {
    setTicketForm({
      email: "",
      customerName: "",
      subject: "",
      content: "",
      priority: "normal",
      organizationId: "",
    });
    setIsCreateTicketOpen(false);
  };

  // Profile modal açıldığında user bilgilerini API'den yükle
  useEffect(() => {
    const loadUserProfile = async () => {
      if (isProfileDialogOpen) {
        try {
          // API'den güncel user bilgisini çek
          const profileData = await authService.getProfile();
          const currentUser = profileData.user;

          // console.log('🔄 Loading profile form with user:', currentUser);

          setProfileForm(prev => ({
            ...prev,
            firstName: currentUser.firstName || "",
            lastName: currentUser.lastName || "",
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }));

          // Auth store'u da güncelle
          const { setUser } = useAuthStore.getState();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          console.error('Failed to load user profile:', error);
          // Fallback to local user
          if (user) {
            setProfileForm(prev => ({
              ...prev,
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            }));
          }
        }
      }
    };

    loadUserProfile();
  }, [isProfileDialogOpen]);

  const handleProfileFormChange = (
    field: string,
    value: string | boolean,
  ): void => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (): Promise<void> => {
    try {
      setProfileForm(prev => ({ ...prev, isLoading: true }));

      if (!user?.id) {
        alert(t('views:u.mUserNotFound', 'Benutzer nicht gefunden'));
        setProfileForm(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Check if ANY password field is filled
      const hasPasswordInput = !!(profileForm.currentPassword || profileForm.newPassword || profileForm.confirmPassword);

      // Password validation - ONLY if user is trying to change password
      if (hasPasswordInput) {
        if (!profileForm.currentPassword) {
          alert(t('views:u.mCurrentPw', 'Bitte geben Sie Ihr aktuelles Passwort ein'));
          setProfileForm(prev => ({ ...prev, isLoading: false }));
          return;
        }
        if (!profileForm.newPassword) {
          alert(t('views:u.mNewPw', 'Bitte geben Sie ein neues Passwort ein'));
          setProfileForm(prev => ({ ...prev, isLoading: false }));
          return;
        }
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          alert(t('views:u.mPwMismatch', 'Die Passwörter stimmen nicht überein'));
          setProfileForm(prev => ({ ...prev, isLoading: false }));
          return;
        }
        if (profileForm.newPassword.length < 6) {
          alert(t('views:u.mPwMin6', 'Das Passwort muss mindestens 6 Zeichen lang sein'));
          setProfileForm(prev => ({ ...prev, isLoading: false }));
          return;
        }
      }

      let profileUpdated = false;
      let passwordChanged = false;

      // 1. Update profile (name) - only if changed
      const nameChanged = profileForm.firstName !== user.firstName || profileForm.lastName !== user.lastName;
      if (nameChanged) {
        await usersService.updateUser(user.id, {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
        });
        profileUpdated = true;
      }

      // 2. Change password if provided
      if (hasPasswordInput) {
        await authService.changePassword(
          profileForm.currentPassword,
          profileForm.newPassword
        );
        passwordChanged = true;
      }

      // If nothing changed
      if (!profileUpdated && !passwordChanged) {
        alert(t('views:u.mNoChanges', 'Keine Änderungen vorgenommen'));
        setProfileForm(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Refresh user profile
      const updatedProfile = await authService.getProfile();
      localStorage.setItem('user', JSON.stringify(updatedProfile.user));

      // Update auth store
      const { setUser } = useAuthStore.getState();
      setUser(updatedProfile.user);

      alert(t('views:u.mProfileUpdated', 'Profil erfolgreich aktualisiert') + (passwordChanged ? '\n\nSie werden in Kürze abgemeldet.' : ''));
      setIsProfileDialogOpen(false);

      // If password changed, logout after 2 seconds
      if (passwordChanged) {
        setTimeout(async () => {
          await handleLogout();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Profile save error:', error);
      const errorMessage = error?.response?.data?.message || 'Fehler beim Speichern des Profils';
      alert(errorMessage);
    } finally {
      setProfileForm(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleCancelProfile = (): void => {
    setProfileForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      isLoading: false,
    });
    setIsProfileDialogOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to login
      window.location.href = '/login';
    }
  };

  return (
    <div className={cn("vyndesk-shell h-screen bg-background flex flex-col overflow-hidden")}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-white text-sm font-medium shrink-0 z-50">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span>
              {t('views:u.uSimActive', 'Simulation aktiv: Sie sind als')} <strong>{user?.firstName} {user?.lastName}</strong> ({user?.email}) angemeldet.
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-3 text-white hover:bg-amber-600 hover:text-white border border-white/30 text-xs font-semibold"
            onClick={stopImpersonation}
          >
            {t('views:u.uSimEnd', 'Simulation beenden')}
          </Button>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
      {/* Left Sidebar - Desktop */}
      {!isSettingsRoute && (
      <aside
        className={cn(
          "vyndesk-primary-sidebar relative hidden h-full overflow-hidden bg-[#0D0D0D] md:flex flex-col transition-all duration-300 ease-in-out z-20 border-r border-white/[0.07]",
          isSidebarCollapsed ? "w-[68px]" : isEditingViews ? "w-[340px]" : "w-[280px]"
        )}
      >
        {/* Modern clean background without heavy gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />
        
        <div className="relative flex h-full w-full flex-col">
          {/* Sidebar Header */}
          <div className={cn("flex items-center h-[70px] px-6 transition-all duration-300", isSidebarCollapsed ? "justify-center px-0" : "justify-between")}>
            {!isSidebarCollapsed && (
              <img
                src={isDarkMode ? "/brand/vyndesk-logo-light.png" : "/brand/vyndesk-logo.png"}
                alt="Vyndesk"
                className="h-7 w-auto object-contain"
              />
            )}
            {isSidebarCollapsed && (
              <img
                src="/brand/vyndesk-mark.png"
                alt="Vyndesk"
                className="h-7 w-7 object-contain"
              />
            )}
            {!isSidebarCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="transform rotate-180">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </Button>
            )}
          </div>

          {/* Toggle Button for Collapsed State */}
          {isSidebarCollapsed && (
            <div className="flex justify-center mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </Button>
            </div>
          )}

          {/* Sidebar Navigation */}
          {/* Die nav scrollt NICHT mehr selbst: Ansichten und Angeheftetes
              bekommen je eine eigene Scrollfläche, damit eine lange Liste die
              andere nicht wegdrückt. Deshalb hier overflow-hidden + flex-col. */}
          <nav className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2">
            <div className="flex min-h-0 flex-1 flex-col gap-1">
              {/* Dashboard Link */}
              <div className="mb-1">
                <Link to="/dashboard">
                  <span
                    className={cn(
                      "group flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-all duration-200",
                      isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full",
                      location.pathname === '/dashboard'
                        ? "bg-white/[0.10] text-white ring-1 ring-inset ring-white/[0.08]"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    )}
                    title={isSidebarCollapsed ? "Dashboard" : undefined}
                  >
                    <BarChart3 className={cn("flex-shrink-0 transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-5 w-5" : "h-4 w-4")} />
                    {!isSidebarCollapsed && (
                      <span className="text-xs font-medium">{t('shell:nav.dashboard', 'Dashboard')}</span>
                    )}
                  </span>
                </Link>
              </div>

              {/* KI-Entwürfe (Review Inbox) */}
              <div className="mb-1">
                <Link to="/ki-entwuerfe">
                  <span
                    className={cn(
                      "group flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-all duration-200",
                      isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full",
                      location.pathname === '/ki-entwuerfe'
                        ? "bg-white/[0.10] text-white ring-1 ring-inset ring-white/[0.08]"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    )}
                    title={isSidebarCollapsed ? "KI-Entwürfe" : undefined}
                  >
                    <img src={agentIcon} alt="" className={cn("flex-shrink-0 object-contain transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-5 w-5" : "h-4 w-4")} />
                    {!isSidebarCollapsed && <span className="text-xs font-medium">{t('shell:nav.kiDrafts', 'KI-Entwürfe')}</span>}
                    {kiDraftCount > 0 && (
                      <span className={cn(
                        "sidebar-count-badge ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/[0.14] text-[10px] font-bold leading-none text-white",
                        location.pathname === '/ki-entwuerfe' && "bg-white/25",
                        isSidebarCollapsed && "absolute -mt-4 ml-4"
                      )}>
                        {kiDraftCount}
                      </span>
                    )}
                  </span>
                </Link>
              </div>

              {/* Kalender */}
              <div className="mb-1">
                <Link to="/kalender">
                  <span
                    className={cn(
                      "group flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-all duration-200",
                      isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full",
                      location.pathname === '/kalender'
                        ? "bg-white/[0.10] text-white ring-1 ring-inset ring-white/[0.08]"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    )}
                    title={isSidebarCollapsed ? t('shell:cal.title', 'Kalender') : undefined}
                  >
                    <CalendarDays className={cn("flex-shrink-0 transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-5 w-5" : "h-4 w-4")} />
                    {!isSidebarCollapsed && <span className="text-xs font-medium">{t('shell:cal.title', 'Kalender')}</span>}
                  </span>
                </Link>
              </div>

              {/* Views rendering with owned/shared sections — eigene Scrollfläche */}
              <div className="vyndesk-sidebar-section sidebar-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
              {(() => {
                const displayViews = isEditingViews ? editingViews : views;
                const ownedViews = displayViews.filter(v => !v.isCustom || v.isOwner !== false);
                const sharedViews = displayViews.filter(v => v.isCustom && v.isOwner === false);

                const renderViewItem = (item: ViewItem, index: number, isSharedSection: boolean = false) => {
                  const IconComponent = item.icon;
                  const targetPath = item.url || item.path || `/tickets/${item.view}`;
                  const isExternalUrl = item.url && (item.url.startsWith('http://') || item.url.startsWith('https://'));

                  // Check if this view is active
                  const isActive = (() => {
                    if (isExternalUrl) return false;
                    if (targetPath.includes('?')) {
                      const [pathPart, queryPart] = targetPath.split('?');
                      const urlParams = new URLSearchParams(location.search);
                      const targetParams = new URLSearchParams(queryPart);
                      if (location.pathname !== pathPart) return false;
                      for (const [key, value] of targetParams.entries()) {
                        if (urlParams.get(key) !== value) return false;
                      }
                      return true;
                    }
                    if (location.pathname === targetPath) {
                      return location.search === '';
                    }
                    return false;
                  })();

                  // For editing mode — skip hardcoded views (e.g. Alle Tickets)
                  if (isEditingViews) {
                    if (!item.isCustom) return null;
                    const isOwner = item.isOwner !== false;
                    const nameChanged = isOwner && item.viewId && originalViewNames[item.viewId] !== item.name;
                    return (
                      <li key={`${item.view}-${index}`}>
                        <div
                          className={cn(
                            "group/item flex items-center gap-1.5 rounded-lg px-1.5 py-1 mb-0.5 transition-colors",
                            isOwner ? "hover:bg-white/[0.04]" : "opacity-50"
                          )}
                          draggable={isOwner}
                          onDragStart={() => isOwner && handleDragStart(index)}
                          onDragOver={(e) => isOwner && handleDragOver(e, index)}
                          onDrop={(e) => isOwner && handleDrop(e, index)}
                        >
                          {isOwner ? (
                            <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-slate-600 cursor-grab active:cursor-grabbing" />
                          ) : (
                            <div className="w-3.5 flex-shrink-0" />
                          )}
                          {isOwner ? (
                            <Input
                              value={item.name}
                              onChange={(e) => handleViewNameChange(index, e.target.value)}
                              className="flex-1 h-7 rounded-md border-0 bg-white/[0.06] text-[11px] text-slate-200 placeholder:text-slate-500 focus:bg-white/[0.1] focus:ring-1 focus:ring-white/20 px-2"
                            />
                          ) : (
                            <span className="flex-1 text-[11px] text-slate-500 truncate px-2">{item.name}</span>
                          )}
                          {isOwner && (
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {nameChanged && (
                                <button
                                  className="flex h-6 w-6 items-center justify-center rounded-md text-emerald-400 hover:bg-emerald-500/15 transition-colors"
                                  onClick={() => handleUpdateViewName(item.viewId!, item.name)}
                                  disabled={updateCustomViewMutation.isPending}
                                  title={t('shell:views.saveName', 'Name speichern')}
                                >
                                  {updateCustomViewMutation.isPending ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Check className="h-3 w-3" />
                                  )}
                                </button>
                              )}
                              {item.viewId && (
                                <>
                                  <button
                                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                                    onClick={() => handleShareView(item)}
                                    title={t('shell:views.share', 'Teilen')}
                                  >
                                    <Share2 className="h-3 w-3" />
                                  </button>
                                  <button
                                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                                    onClick={() => handleEditViewConfig(item)}
                                    title={t('shell:views.editFilter', 'Filter bearbeiten')}
                                  >
                                    <Settings className="h-3 w-3" />
                                  </button>
                                </>
                              )}
                              <button
                                className="flex h-6 w-6 items-center justify-center rounded-md text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                onClick={() => handleRemoveView(index)}
                                title={t('shell:views.deleteAction', 'Löschen')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                          {!isOwner && (
                            <span className="text-[9px] text-slate-600 flex items-center gap-1 flex-shrink-0">
                              {item.accessType === 'all' ? <Globe className="h-2.5 w-2.5" /> : <Share2 className="h-2.5 w-2.5" />}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  }

                  // Normal display mode
                  return (
                    <li key={`${item.view}-${index}`}>
                      {isExternalUrl ? (
                        <a href={targetPath} target="_blank" rel="noopener noreferrer">
                          <span
                            className={cn(
                              "group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all duration-200",
                              isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full justify-between",
                              "text-slate-400 hover:bg-slate-800 hover:text-white hover:pl-3"
                            )}
                            title={isSidebarCollapsed ? item.name : undefined}
                          >
                            <span className={cn("flex items-center gap-2", isSidebarCollapsed && "justify-center")}>
                              {IconComponent && <IconComponent className={cn("flex-shrink-0 transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-4 w-4" : "h-3.5 w-3.5")} />}
                              {!isSidebarCollapsed && <span className="text-xs font-medium">{item.name}</span>}
                            </span>
                            {!isSidebarCollapsed && (item.isCustom || item.view === 'all') && (
                              countsLoaded ? (
                                (viewCounts[item.view] ?? 0) > 0 && (
                                  <Badge variant="secondary" className="sidebar-count-badge rounded-md text-[10px] border-0 font-normal px-1.5">
                                    {viewCounts[item.view] ?? 0}
                                  </Badge>
                                )
                              ) : (
                                <Skeleton className="h-4 w-5 rounded-md bg-slate-800" />
                              )
                            )}
                          </span>
                        </a>
                      ) : (
                        <Link to={targetPath}>
                          <span
                            className={cn(
                              "group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all duration-200",
                              isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full justify-between",
                              isActive
                                ? "bg-white/[0.09] text-white border-l-2 border-white/40 rounded-l-none pl-[8px]"
                                : "text-slate-400 hover:bg-slate-800 hover:text-white hover:pl-3"
                            )}
                            title={isSidebarCollapsed ? item.name : undefined}
                          >
                            <span className={cn("flex items-center gap-2 min-w-0", isSidebarCollapsed && "justify-center")}>
                              {IconComponent && <IconComponent className={cn("flex-shrink-0 transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-4 w-4" : "h-3.5 w-3.5", isActive && "text-slate-100")} />}
                              {!isSidebarCollapsed && (
                                <span className="text-xs truncate">{item.name}</span>
                              )}
                              {!isSidebarCollapsed && isSharedSection && item.accessType === 'all' && (
                                <Globe className="h-2.5 w-2.5 text-blue-400 flex-shrink-0 opacity-70" title={t('shell:views.sharedAll', 'Geteilt mit allen')} />
                              )}
                            </span>
                            {!isSidebarCollapsed && (
                              <div className="flex items-center gap-1.5">
                                {(item.isCustom || item.view === 'all') && (
                                  countsLoaded ? (
                                    (viewCounts[item.view] ?? 0) > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className={cn(
                                          "sidebar-count-badge rounded-md text-[10px] border-0 font-normal px-1.5",
                                          isActive
                                            ? "bg-white/[0.14] text-white"
                                            : "bg-slate-800 text-slate-300 group-hover:bg-slate-700 group-hover:text-white"
                                        )}
                                      >
                                        {viewCounts[item.view] ?? 0}
                                      </Badge>
                                    )
                                  ) : (
                                    <Skeleton className="h-4 w-5 rounded-md bg-slate-800" />
                                  )
                                )}
                              </div>
                            )}
                          </span>
                        </Link>
                      )}
                    </li>
                  );
                };

                return (
                  <>
                    {/* My Views Section */}
                    {!isSidebarCollapsed && (
                      isEditingViews ? (
                        <div className="mt-1 mb-3 mx-1 flex items-center justify-between rounded-lg bg-white/[0.06] px-3 py-2 border border-white/[0.08]">
                          <div className="flex items-center gap-2">
                            <div className="flex h-5 w-5 items-center justify-center rounded bg-white/[0.08]">
                              <Edit3 className="h-2.5 w-2.5 text-slate-300" />
                            </div>
                            <span className="text-[11px] font-semibold text-white/90">{t('shell:views.edit', 'Ansichten bearbeiten')}</span>
                          </div>
                          <button
                            onClick={handleCancelEdit}
                            className="flex h-5 w-5 items-center justify-center rounded-md text-slate-500 hover:bg-white/10 hover:text-white transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="mt-1 mb-2 flex items-center justify-between px-2.5">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            {sharedViews.length > 0 ? 'Meine Ansichten' : 'Ansichten'}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-md text-slate-500 hover:bg-slate-800 hover:text-white transition-colors"
                            onClick={handleEditViews}
                          >
                            <Edit3 className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                      )
                    )}
                    <ul className="space-y-0.5">
                      {ownedViews.map((item, index) => renderViewItem(item, index, false))}
                    </ul>

                    {/* Shared With Me Section */}
                    {sharedViews.length > 0 && (
                      <>
                        {!isSidebarCollapsed && (
                          <div className="mt-5 mb-2 flex items-center justify-between px-2.5">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
                              <Share2 className="h-2.5 w-2.5" />
                              {t('views:u.uSharedWithMe', 'Mit mir geteilt')}
                            </p>
                          </div>
                        )}
                        <ul className="space-y-0.5">
                          {sharedViews.map((item, index) => {
                            const viewWithOwnerInfo = {
                              ...item,
                              name: !isSidebarCollapsed && item.ownerFirstName
                                ? `${item.name}`
                                : item.name
                            };
                            return (
                              <li key={`shared-${item.view}-${index}`}>
                                <Link to={item.path || `/tickets/${item.view}`}>
                                  <span
                                    className={cn(
                                      "group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all duration-200",
                                      isSidebarCollapsed ? "justify-center w-10 h-10 mx-auto" : "w-full justify-between",
                                      location.pathname === (item.path || `/tickets/${item.view}`)
                                        ? "bg-white/[0.09] text-white border-l-2 border-white/40 rounded-l-none pl-[8px]"
                                        : "text-slate-400 hover:bg-slate-800 hover:text-white hover:pl-3"
                                    )}
                                    title={isSidebarCollapsed ? `${item.name} (von ${item.ownerFirstName} ${item.ownerLastName})` : undefined}
                                  >
                                    <span className={cn("flex items-center gap-2 min-w-0", isSidebarCollapsed && "justify-center")}>
                                      {item.icon && <item.icon className={cn("flex-shrink-0 transition-transform duration-200 group-hover:scale-110", isSidebarCollapsed ? "h-4 w-4" : "h-3.5 w-3.5", location.pathname === (item.path || `/tickets/${item.view}`) && "text-slate-100")} />}
                                      {!isSidebarCollapsed && (
                                        <div className="flex flex-col min-w-0">
                                          <span className="text-xs truncate">{item.name}</span>
                                          <span className="text-[8px] text-slate-500 truncate font-normal">
                                            {t('ticketList:tl.of', 'von')} {item.ownerFirstName} {item.ownerLastName}
                                          </span>
                                        </div>
                                      )}
                                    </span>
                                    {!isSidebarCollapsed && (
                                      <div className="flex items-center gap-1.5">
                                        {item.accessType === 'all' && (
                                          <Globe className="h-2.5 w-2.5 text-blue-400 flex-shrink-0 opacity-70" title={t('shell:views.sharedAll', 'Geteilt mit allen')} />
                                        )}
                                        {countsLoaded ? (
                                          (viewCounts[item.view] ?? 0) > 0 && (
                                            <Badge
                                              variant="secondary"
                                              className={cn(
                                                "sidebar-count-badge rounded-md text-[10px] border-0 font-normal px-1.5",
                                                location.pathname === (item.path || `/tickets/${item.view}`)
                                                  ? "bg-white/[0.14] text-white"
                                                  : "bg-slate-800 text-slate-300 group-hover:bg-slate-700 group-hover:text-white"
                                              )}
                                            >
                                              {viewCounts[item.view] ?? 0}
                                            </Badge>
                                          )
                                        ) : (
                                          <Skeleton className="h-4 w-5 rounded-md bg-slate-800" />
                                        )}
                                      </div>
                                    )}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </>
                );
              })()}
              </div>

              {/* Angeheftete Tickets — eigene Scrollfläche + eigene Suche.
                  Im Bearbeitungsmodus der Ansichten ausgeblendet, damit die
                  Seitenleiste dort nicht zwei konkurrierende Listen zeigt. */}
              {/* Angeheftete Tickets — bewusst ausgeblendet (Backend + Komponente
                  bleiben; bei Bedarf einfach wieder einkommentieren):
                  {!isEditingViews && <PinnedTicketsSection collapsed={isSidebarCollapsed} />} */}

              {isEditingViews && (
                <div className="mt-2 mx-1 space-y-2">
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleCreateView}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] hover:border-white/[0.15] text-[11px] text-slate-300 hover:text-white h-8 transition-all"
                    >
                      <Plus className="h-3 w-3" />
                      {t('views:u.uNewView', 'Neue Ansicht')}
                    </button>
                    <button
                      onClick={handleResetToDefault}
                      className="flex items-center justify-center gap-1.5 rounded-md bg-white/[0.04] hover:bg-orange-500/10 border border-white/[0.06] hover:border-orange-500/20 text-[11px] text-slate-500 hover:text-orange-300 h-8 px-3 transition-all"
                      title={t('shell:views.reset', 'Auf Standard zurücksetzen')}
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 flex items-center justify-center rounded-md border border-white/[0.08] text-[11px] text-slate-400 hover:text-white hover:bg-white/[0.06] h-8 transition-all"
                    >
                      {t('common:action.cancel', 'Abbrechen')}
                    </button>
                    <button
                      onClick={handleSaveViews}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-white/[0.12] hover:bg-white/[0.18] border border-white/[0.08] text-[11px] font-medium text-white h-8 transition-all"
                    >
                      <Save className="h-3 w-3" />
                      {t('common:action.save', 'Speichern')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Telefon — immer sichtbar, unabhängig von Route und Einrichtung.
              Ist die Telefonie noch nicht konfiguriert, zeigt die Karte das an,
              statt zu verschwinden. */}
          {/* Meetings — Schnellzugriff, gleicher Look wie die Telefonkarte */}
          <div className={isSidebarCollapsed ? "flex justify-center px-3 pb-2" : "px-3 pb-2"}>
            <MeetingsCard collapsed={isSidebarCollapsed} />
          </div>

          <div className={isSidebarCollapsed ? "flex justify-center px-3 pb-2" : "px-3 pb-2"}>
            <CallStatus collapsed={isSidebarCollapsed} />
          </div>

          {/* WhatsApp — nur sichtbar, wenn eine Organisation verbunden ist */}
          <div className={isSidebarCollapsed ? "flex justify-center px-3 pb-2" : "px-3 pb-2"}>
            <WhatsAppCard collapsed={isSidebarCollapsed} />
          </div>

          {/* Cloud-Agent Umschalter + Live-Status (Admin + Agent) */}
          {(user?.role === "admin" || user?.role === "agent") && (
            <div className={isSidebarCollapsed ? "flex justify-center px-3 pb-1" : "px-3"}>
              <CloudAgentToggle collapsed={isSidebarCollapsed} />
            </div>
          )}

          {/* User Profile Section */}
          <div className="px-3 pb-3">
            {!isSidebarCollapsed ? (
              <div className="vyndesk-sidebar-profile flex items-center justify-between rounded-md bg-white/5 px-2.5 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-medium text-white/90">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="truncate text-[10px] text-white/50">
                    {user?.role === "admin" ? "Admin" : user?.role === "agent" ? "Agent" : "User"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                    onClick={toggleDarkMode}
                    title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                  >
                    {isDarkMode ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-6 w-6 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                    onClick={() => navigate('/changelog')}
                    title={t('shell:action.changelog', 'Changelog')}
                  >
                    <Megaphone className="h-3 w-3" />
                    {changelogUnread && (
                      <span className="absolute right-0 top-0 flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sky-400" />
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                    onClick={handleReportBug}
                    disabled={isReportingBug}
                    title={t('shell:action.reportBug', 'Fehler melden')}
                  >
                    <Bug className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                    onClick={() => navigate('/tickets/spam')}
                    title={t('shell:action.archive', 'Archiv')}
                  >
                    <Archive className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
                    onClick={() => navigate('/settings')}
                    title={t('shell:action.settings', 'Einstellungen')}
                  >
                    <Settings className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                  onClick={toggleDarkMode}
                  title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                >
                  {isDarkMode ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-7 w-7 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/changelog')}
                  title={t('shell:action.changelog', 'Changelog')}
                >
                  <Megaphone className="h-3.5 w-3.5" />
                  {changelogUnread && (
                    <span className="absolute right-0.5 top-0.5 flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sky-400" />
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                  onClick={handleReportBug}
                  disabled={isReportingBug}
                  title={t('shell:action.reportBug', 'Fehler melden')}
                >
                  <Bug className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded text-slate-400 hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/tickets/spam')}
                  title={t('shell:action.archive', 'Archiv')}
                >
                  <Archive className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
                  onClick={() => navigate('/settings')}
                  title={t('shell:action.settings', 'Einstellungen')}
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </aside>
      )}

      {/* Mobile Menu Button */}
      {!isSettingsRoute && (
      <div className="md:hidden absolute top-4 left-4 z-50">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0">
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="text-xl font-bold text-red-700">
                  SupportHub
                </div>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 px-4 py-2">
                <div className="mt-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {t('views:u.uViews', 'Ansichten')}
                  </p>
                  <ul className="space-y-1">
                    {views.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <li key={index}>
                          <Link
                            to={item.path || `/tickets/${item.view}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Button
                              variant="ghost"
                              className={cn(
                                "w-full justify-between h-8",
                                location.pathname === (item.path || `/tickets/${item.view}`)
                                  ? "bg-accent text-accent-foreground"
                                  : "hover:bg-accent/50",
                              )}
                            >
                              <div className="flex items-center">
                                {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                                {item.name}
                              </div>
                              {(item.isCustom || item.view === 'all') && (
                                countsLoaded ? (
                                  (viewCounts[item.view] ?? 0) > 0 && (
                                    <Badge
                                      variant={item.urgent ? "destructive" : "secondary"}
                                      className="h-5 text-xs"
                                    >
                                      {viewCounts[item.view] ?? 0}
                                    </Badge>
                                  )
                                ) : (
                                  <Skeleton className="h-5 w-6 rounded-md" />
                                )
                              )}
                            </Button>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </nav>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      )}

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header with Search - Hide on ticket detail page & dashboard */}
        {showTopHeader && (
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-3">
          {/* Ticket Tabs - show only when tabs are open */}
          {openTickets.length > 0 ? (
            <div className="flex-1 min-w-0 overflow-hidden">
              <TicketTabsBar />
            </div>
          ) : (
            <div className="flex-1" />
          )}

          {/* Right side actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Animated Search */}
            <div className="relative flex items-center">
              <div
                className={cn(
                  "flex items-center transition-all duration-300 ease-in-out overflow-hidden",
                  isSearchExpanded ? "w-[420px]" : "w-9"
                )}
              >
                {isSearchExpanded ? (
                  <div className="relative w-full">
                    <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <Search className="h-4 w-4" />
                    </div>
                    <Input
                      ref={searchInputRef}
                      data-voice="search"
                      placeholder={t('shell:search.placeholder', 'Ticket/Referenznr./E-Mail suchen...')}
                      className="w-full h-9 rounded-lg border-slate-200 bg-white pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-[#0F99FD] focus-visible:border-[#0F99FD] focus-visible:bg-white"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (searchQuery.trim()) {
                            setShowSearchResults(false);
                            navigate(`/tickets/search?q=${encodeURIComponent(searchQuery)}`);
                          }
                        } else if (e.key === 'Escape') {
                          setShowSearchResults(false);
                          setSearchQuery('');
                          setIsSearchExpanded(false);
                        }
                      }}
                      onFocus={() => {
                        if (searchResults.length > 0) {
                          setShowSearchResults(true);
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        setIsSearchExpanded(false);
                        setSearchQuery('');
                        setShowSearchResults(false);
                      }}
                      className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {isSearching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                    onClick={() => {
                      setIsSearchExpanded(true);
                    }}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900",
                    location.pathname.startsWith("/settings") && "bg-slate-100 text-slate-900 border-slate-300"
                  )}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 rounded-xl border border-slate-100 bg-white shadow-lg p-1" align="end">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-slate-100 my-1" />
                <DropdownMenuItem
                  onClick={() => setIsProfileDialogOpen(true)}
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50 focus:text-slate-900"
                >
                  <User className="mr-2 h-4 w-4 text-slate-400" />
                  {t('views:u.uEditProfile', 'Profil bearbeiten')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50 focus:text-slate-900"
                >
                  <Settings className="mr-2 h-4 w-4 text-slate-400" />
                  {t('views:u.uSettings', 'Einstellungen')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-100 my-1" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 focus:bg-red-50 focus:text-red-700"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('views:u.uLogout', 'Abmelden')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        )}

        {/* Main Content */}
        <main className="vyndesk-main flex-1 bg-muted/30 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={isCreateTicketOpen} onOpenChange={setIsCreateTicketOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('shell:action.newTicket', 'Neues Ticket erstellen')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ticket-email" className="text-right">
                E-Mail *
              </Label>
              <Input
                id="ticket-email"
                type="email"
                value={ticketForm.email}
                onChange={(e) => handleTicketFormChange("email", e.target.value)}
                placeholder={t('shell:ph.customerEmail', 'kunde@beispiel.de')}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ticket-customerName" className="text-right">
                {t('ticket:section.customerName', 'Kundenname')}
              </Label>
              <Input
                id="ticket-customerName"
                value={ticketForm.customerName}
                onChange={(e) => handleTicketFormChange("customerName", e.target.value)}
                placeholder={t('shell:ph.fullName', 'Max Mustermann')}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ticket-organization" className="text-right">
                {t('views:u.uOrgReq', 'Organization *')}
              </Label>
              <Select
                value={ticketForm.organizationId}
                onValueChange={(value) => handleTicketFormChange("organizationId", value)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder={t('shell:ph.org', 'Organization auswählen')} />
                </SelectTrigger>
                <SelectContent>
                  {orgsResponse?.data?.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ticket-priority" className="text-right">
                {t('ticketList:column.priority', 'Priorität')}
              </Label>
              <Select
                value={ticketForm.priority}
                onValueChange={(value) => handleTicketFormChange("priority", value)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('domain:priority.low', 'Niedrig')}</SelectItem>
                  <SelectItem value="normal">{t('domain:priority.normal', 'Normal')}</SelectItem>
                  <SelectItem value="high">{t('domain:priority.high', 'Hoch')}</SelectItem>
                  <SelectItem value="urgent">{t('domain:priority.urgent', 'Dringend')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ticket-subject" className="text-right">
                {t('views:u.uSubjectReq', 'Betreff *')}
              </Label>
              <Input
                id="ticket-subject"
                value={ticketForm.subject}
                onChange={(e) => handleTicketFormChange("subject", e.target.value)}
                placeholder={t('shell:ph.subject', 'Betreff eingeben')}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="ticket-content" className="text-right pt-2">
                {t('views:u.uMessageReq', 'Nachricht *')}
              </Label>
              <Textarea
                id="ticket-content"
                value={ticketForm.content}
                onChange={(e) => handleTicketFormChange("content", e.target.value)}
                placeholder={t('shell:ph.content', 'Ticket-Inhalt eingeben...')}
                className="col-span-3 min-h-[100px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelTicket}
              disabled={createTicketMutation.isPending}
            >
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={handleSendTicket}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              disabled={!ticketForm.email || !ticketForm.subject || !ticketForm.content || !ticketForm.organizationId || createTicketMutation.isPending}
            >
              {createTicketMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common:state.creating', 'Wird erstellt...')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('views:u.uSendTicket', 'Ticket senden')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create View Dialog - New Advanced Version */}
      <CreateCustomViewDialog
        open={isCreateViewOpen}
        onOpenChange={(open) => {
          setIsCreateViewOpen(open);
          if (!open) {
            setEditingViewData(null);
          }
        }}
        editView={editingViewData}
        onSuccess={() => {
          // Reload views after creating/updating
          setEditingViewData(null);
          window.location.reload();
        }}
      />

      {/* Share View Dialog */}
      <ShareViewDialog
        open={!!shareViewDialog}
        onOpenChange={(open) => !open && setShareViewDialog(null)}
        viewId={shareViewDialog?.viewId || null}
        viewName={shareViewDialog?.viewName || ''}
      />

      {/* Delete Custom View Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmView} onOpenChange={(open) => !open && setDeleteConfirmView(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shell:views.delete', 'Ansicht löschen')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('views:u.uDeleteViewQ', 'Möchten Sie die Ansicht "')}{deleteConfirmView?.view.name}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('shell:action.cancel', 'Abbrechen')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteView}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteViewMutation.isPending}
            >
              {deleteViewMutation.isPending ? 'Löschen...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('shell:action.editProfile', 'Profil bearbeiten')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="profile-firstName">{t('shell:profile.firstName', 'Vorname')}</Label>
              <Input
                id="profile-firstName"
                value={profileForm.firstName}
                onChange={(e) => handleProfileFormChange("firstName", e.target.value)}
                placeholder={t('shell:profile.firstName', 'Vorname')}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="profile-lastName">{t('shell:profile.lastName', 'Nachname')}</Label>
              <Input
                id="profile-lastName"
                value={profileForm.lastName}
                onChange={(e) => handleProfileFormChange("lastName", e.target.value)}
                placeholder={t('shell:profile.lastName', 'Nachname')}
              />
            </div>

            <Separator className="my-2" />

            <p className="text-sm text-slate-600">
              Lassen Sie die Passwortfelder leer, wenn Sie Ihr Passwort nicht ändern möchten.
            </p>

            <div className="grid gap-2">
              <Label htmlFor="current-password">{t('shell:profile.currentPassword', 'Aktuelles Passwort')}</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={profileForm.currentPassword}
                  onChange={(e) =>
                    handleProfileFormChange("currentPassword", e.target.value)
                  }
                  className="pr-10"
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="new-password">{t('shell:profile.newPassword', 'Neues Passwort')}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={profileForm.newPassword}
                  onChange={(e) =>
                    handleProfileFormChange("newPassword", e.target.value)
                  }
                  className="pr-10"
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="confirm-password">{t('shell:profile.confirmPassword', 'Passwort bestätigen')}</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={profileForm.confirmPassword}
                  onChange={(e) =>
                    handleProfileFormChange("confirmPassword", e.target.value)
                  }
                  className="pr-10"
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelProfile}>
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={handleSaveProfile}
              disabled={profileForm.isLoading}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
            >
              {profileForm.isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common:state.saving', 'Wird gespeichert...')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('common:action.save', 'Speichern')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Results Dropdown Portal */}
      {showSearchResults && dropdownPosition && createPortal(
        <div
          ref={searchResultsRef}
          className="fixed z-[99999] max-h-[400px] overflow-y-auto rounded-xl border border-slate-100 bg-white shadow-xl ring-1 ring-slate-200/50"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              {t('views:u.uNoResults', 'Keine Ergebnisse gefunden')}
            </div>
          ) : (
            searchResults.map((ticket) => (
            <div
              key={ticket.id}
              className="group cursor-pointer border-b border-slate-100 px-4 py-3 hover:bg-slate-50 last:border-0"
              onClick={() => {
                setShowSearchResults(false);
                setSearchQuery('');
                navigate(`/tickets/${ticket.id}`);
              }}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium text-slate-900 group-hover:text-slate-700 truncate">
                  {ticket.subject}
                </span>
                <span className="ml-2 text-[10px] font-mono text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                  {ticket.ticketNumber}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="font-medium">{ticket.requester.name}</span>
                <span className="text-slate-300">•</span>
                <span className="truncate max-w-[120px]">{ticket.organization.name}</span>
                <span className="text-slate-300">•</span>
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${
                    ticket.status === 'open'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : ticket.status === 'pending'
                      ? 'bg-amber-50 text-amber-700 border-amber-100'
                      : ticket.status === 'closed'
                      ? 'bg-slate-50 text-slate-600 border-slate-100'
                      : 'bg-blue-50 text-blue-700 border-blue-100'
                  }`}
                >
                  {ticket.status === 'open' ? 'Offen' :
                   ticket.status === 'pending' ? 'Wartend' :
                   ticket.status === 'closed' ? 'Geschlossen' : 'Neu'}
                </span>
              </div>
            </div>
          )))}
          {searchQuery.trim() && searchResults.length > 0 && (
            <div className="sticky bottom-0 border-t border-slate-100 bg-slate-50/80 backdrop-blur-sm p-2 text-center">
              <button
                className="w-full rounded-lg bg-white border border-slate-200 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors shadow-sm"
                onClick={() => {
                  setShowSearchResults(false);
                  navigate(`/tickets/search?q=${encodeURIComponent(searchQuery)}`);
                }}
              >
                {t('views:u.uShowAllResults', 'Alle Ergebnisse anzeigen')}
              </button>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Sesli Copilot (voice agent) — şimdilik gizli (geliştirme aşamasında).
          Tekrar açmak için: <VoiceAgent /> */}
      {/* <VoiceAgent /> */}

      {/* Agent Cockpit köprüsü — desktop app socket ile web'i sürer (cursor + neon). */}
      <AgentBridge />
    </div>
    </div>
  );
};

export default AppShell;
